// "What's new" posts, written by admins in the Admin Panel and stored on the server
// (api/WhatsNew, tables dbo.WhatsNewPosts and dbo.WhatsNewReads).
// Shown in the home screen sidebar (dashboard.rightarea.component/WhatsNew.jsx); edited in
// Administration/WhatsNewAdmin.jsx.
//
// A post can have a headline, a short message, a banner picture, a button link, and lists of
// changes grouped into New / Improved / Fixed. Write for customers: short, plain words.

import axios from "axios";

export const SECTIONS = [
  { key: "new", label: "New" },
  { key: "improved", label: "Improved" },
  { key: "fixed", label: "Fixed" },
];

// Before the posts moved to the server, "Mark as read" was saved in the browser under this key
// with the old code ids ("2026-10-a"). Still read once, so people who already read the October
// updates are not shown them again as new.
const LEGACY_SEEN_KEY = "toons_whats_new_seen";

function authHeaders() {
  try {
    const auth = JSON.parse(localStorage.getItem("animation_studio_2.0_access_token"));
    return auth && auth.access_token ? { Authorization: "Bearer " + auth.access_token } : {};
  } catch (e) {
    return {};
  }
}

const isWebAddress = (url) => typeof url === "string" && /^https?:\/\//i.test(url.trim());

function parseItems(json) {
  let items = {};
  try {
    items = JSON.parse(json || "{}") || {};
  } catch (e) {
    items = {};
  }
  const out = {};
  for (const s of SECTIONS) {
    out[s.key] = (Array.isArray(items[s.key]) ? items[s.key] : [])
      .filter((i) => i && (i.title || i.text))
      .map((i) => ({ title: String(i.title || ""), text: String(i.text || "") }));
  }
  return out;
}

/** A post as the server sends it -> what the panel and the editor use. */
export function toPost(row) {
  if (!row) return null;
  return {
    id: row.id,
    slug: row.slug || null,
    title: row.title || "",
    message: row.message || "",
    imageUrl: isWebAddress(row.imageUrl) ? row.imageUrl : "",
    linkUrl: isWebAddress(row.linkUrl) ? row.linkUrl : "",
    linkText: row.linkText || "",
    isPublished: !!row.isPublished,
    publishedAt: row.publishedAt || null,
    updatedAt: row.updatedAt || null,
    ...parseItems(row.itemsJson),
  };
}

/** The editor's post -> what the server saves. */
export function toRow(post) {
  const items = {};
  for (const s of SECTIONS) {
    items[s.key] = (post[s.key] || [])
      .map((i) => ({ title: (i.title || "").trim(), text: (i.text || "").trim() }))
      .filter((i) => i.title || i.text);
  }
  return {
    id: post.id || 0,
    title: post.title,
    message: post.message,
    imageUrl: post.imageUrl,
    linkUrl: post.linkUrl,
    linkText: post.linkText,
    itemsJson: JSON.stringify(items),
  };
}

export function emptyPost() {
  return { id: 0, title: "", message: "", imageUrl: "", linkUrl: "", linkText: "", isPublished: false, new: [], improved: [], fixed: [] };
}

export function postHasContent(post) {
  return !!(post && (post.title || post.message || post.imageUrl || SECTIONS.some((s) => (post[s.key] || []).length)));
}

// ------------------------------------------------------------------ everyone

/** { posts (newest first), lastSeenPostId } */
export async function loadWhatsNew(apiBaseURL) {
  const resp = await axios.get(apiBaseURL + "/api/WhatsNew", { headers: authHeaders() });
  if (!resp.data || !resp.data.status) throw new Error((resp.data && resp.data.message) || "What's new could not be loaded.");
  const posts = (resp.data.posts || []).map(toPost).filter(postHasContent);
  let lastSeenPostId = resp.data.lastSeenPostId || null;

  // Read the October updates before they moved to the server: count them as read.
  if (!lastSeenPostId) {
    let legacy = null;
    try {
      legacy = localStorage.getItem(LEGACY_SEEN_KEY);
    } catch (e) {
      legacy = null;
    }
    const match = legacy && posts.find((p) => p.slug === legacy);
    if (match) {
      lastSeenPostId = match.id;
      markWhatsNewRead(apiBaseURL, match.id).catch(() => {});
    }
  }
  return { posts, lastSeenPostId };
}

export async function markWhatsNewRead(apiBaseURL, postId) {
  if (!postId) return;
  await axios.post(apiBaseURL + "/api/WhatsNew/MarkRead", { postId }, { headers: authHeaders() });
}

// ------------------------------------------------------------------ admins

async function adminCall(promise, fallbackMessage) {
  let resp;
  try {
    resp = await promise;
  } catch (e) {
    const status = e && e.response && e.response.status;
    throw new Error(status === 403 || status === 401 ? "Only admins can change What's new." : fallbackMessage);
  }
  if (!resp.data || !resp.data.status) throw new Error((resp.data && resp.data.message) || fallbackMessage);
  return resp.data;
}

export async function adminLoadPosts(apiBaseURL) {
  const data = await adminCall(axios.get(apiBaseURL + "/api/WhatsNew/Admin", { headers: authHeaders() }), "Posts could not be loaded.");
  return (data.posts || []).map(toPost);
}

export async function adminSavePost(apiBaseURL, post) {
  const data = await adminCall(
    axios.post(apiBaseURL + "/api/WhatsNew/Admin/Save", toRow(post), { headers: authHeaders() }),
    "The post could not be saved."
  );
  return toPost(data.post);
}

export async function adminPublishPost(apiBaseURL, id, publish) {
  const data = await adminCall(
    axios.post(apiBaseURL + "/api/WhatsNew/Admin/Publish", { id, publish }, { headers: authHeaders() }),
    "The post could not be updated."
  );
  return toPost(data.post);
}
